// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useEnableTwoFactorMutation, useGenerateTwoFactorMutation } from "@/api/hooks/use-profile";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

interface TwoFactorSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TwoFactorSetupDialog({ open, onOpenChange }: TwoFactorSetupDialogProps) {
  const { t } = useTranslation("back-office");
  const generate = useGenerateTwoFactorMutation();
  const enable = useEnableTwoFactorMutation();
  const [code, setCode] = useState("");

  // Mint a fresh secret + QR each time the dialog opens; clear everything when it closes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: generate/enable are stable react-query handles — only re-run on open/close.
  useEffect(
    function issueSecretOnOpen() {
      if (open) {
        generate.mutate();
      } else {
        setCode("");
        generate.reset();
        enable.reset();
      }
    },
    [open],
  );

  const secret = generate.data?.secret;

  async function onConfirm() {
    if (!secret || code.length < 6) return;
    try {
      await enable.mutateAsync({ secret, token: code });
      toast.success(t("profile.security.mfa.enabled"));
      onOpenChange(false);
    } catch {
      toast.error(t("profile.security.mfa.invalidCode"));
      setCode("");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane icon={ShieldCheck} />
          <div className="flex flex-1 min-w-0 flex-col gap-4 p-6">
            <DialogHeader>
              <DialogTitle>{t("profile.security.mfa.setupTitle")}</DialogTitle>
              <DialogDescription>{t("profile.security.mfa.setupDescription")}</DialogDescription>
            </DialogHeader>

            {generate.isError ? (
              <p className="py-4 text-center text-sm text-destructive">{t("profile.security.mfa.generateError")}</p>
            ) : !secret ? (
              <div className="flex flex-col items-center gap-4 py-2">
                <Skeleton className="h-44 w-44" />
                <Skeleton className="h-4 w-48" />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <img
                  src={generate.data?.qrCode}
                  alt={t("profile.security.mfa.qrAlt")}
                  className="h-44 w-44 rounded-md border bg-white p-2"
                />
                <div className="w-full space-y-1 text-center">
                  <p className="text-xs text-muted-foreground">{t("profile.security.mfa.secretLabel")}</p>
                  <code className="block break-all rounded bg-muted px-2 py-1 font-mono text-xs">{secret}</code>
                </div>
                <div className="w-full space-y-2">
                  <Label className="text-sm">{t("profile.security.mfa.codeLabel")}</Label>
                  <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus containerClassName="w-full">
                    <InputOTPGroup className="w-full">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <InputOTPSlot key={i} index={i} className="h-12 flex-1 text-lg" />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={!secret || code.length < 6 || enable.isPending}>
            {enable.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {t("profile.security.mfa.enable")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
